import { useEffect, useRef, useState } from 'react';

type NumericValue = number | string | null | undefined;

const toText = (value: NumericValue): string =>
    value === null || value === undefined || value === '' ? '' : String(value);

const toNumber = (value: NumericValue): number =>
    typeof value === 'number' ? value : parseFloat(String(value ?? ''));

const sameNumber = (a: NumericValue, b: NumericValue): boolean => {
    const [numA, numB] = [toNumber(a), toNumber(b)];
    return numA === numB || (isNaN(numA) && isNaN(numB));
};

/**
 * Keeps the text the user is typing in a numeric input (e.g. "1." on the way to
 * "1.30") while reporting the parsed number to the parent. Without this, a
 * controlled input that parses on every keystroke drops the trailing decimal
 * point, so cents can never be typed.
 *
 * Returns the text to render and the onChange handler for the input.
 */
export default function useNumericDraft(
    value: NumericValue,
    onChange: (value: number | '') => void
): [string, (raw: string) => void] {
    const [text, setText] = useState<string>(() => toText(value));
    const emitted = useRef<number | '' | null>(null);

    useEffect(() => {
        // Resync only when the value changed outside of typing (e.g. a saved item was picked)
        if (emitted.current === null || !sameNumber(value, emitted.current)) {
            setText(toText(value));
        }
    }, [value]);

    const handleChange = (raw: string) => {
        const parsed = raw === '' ? '' : parseFloat(raw) || 0;
        setText(raw);
        emitted.current = parsed;
        onChange(parsed);
    };

    return [text, handleChange];
}
